import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, ChevronsUp } from "lucide-react";

interface SortOrderCellProps {
    sortOrder: number;
    editTitle: string;
    onEdit: () => void;
    onAdjust: (newSortOrder: number) => Promise<void>;
    disabled?: boolean;
    isUrgentEnqueuePending?: boolean;
    isUrgentEnqueueProcessing?: boolean;
}

type PanelPosition = {
    top: number;
    left: number;
};

const HOVER_HIDE_DELAY_MS = 120;

const ADJUST_BUTTONS = [
    { delta: 1, icon: ChevronUp, title: "우선순위 올리기 (+1)" },
    { delta: -1, icon: ChevronDown, title: "우선순위 내리기 (-1)" },
] as const;

const panelStyle: React.CSSProperties = {
    position: "fixed",
    transform: "translateY(-50%)",
    zIndex: 1050,
    display: "flex",
    flexDirection: "column",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "7px",
    boxShadow: "var(--shadow-card)",
    overflow: "hidden",
};

const adjustButtonStyle: React.CSSProperties = {
    width: 22,
    height: 16,
    lineHeight: 1,
    fontSize: "0.65rem",
};

export const SortOrderCell: React.FC<SortOrderCellProps> = ({
    sortOrder,
    editTitle,
    onEdit,
    onAdjust,
    disabled = false,
    isUrgentEnqueuePending = false,
    isUrgentEnqueueProcessing = false,
}) => {
    const hasUrgentEnqueue = isUrgentEnqueuePending || isUrgentEnqueueProcessing;
    const urgentTitle = isUrgentEnqueueProcessing ? "우선 처리중" : "우선 실행 대기";
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const hideTimeoutRef = React.useRef<number | undefined>(undefined);
    const [panel, setPanel] = React.useState<PanelPosition | null>(null);

    const clearHideTimer = () => window.clearTimeout(hideTimeoutRef.current);

    const dismissPanel = () => {
        clearHideTimer();
        setPanel(null);
    };

    const openPanel = () => {
        if (disabled) {
            return;
        }

        clearHideTimer();
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }

        setPanel({
            top: rect.top + rect.height / 2,
            left: rect.right + 4,
        });
    };

    const scheduleHidePanel = () => {
        clearHideTimer();
        hideTimeoutRef.current = window.setTimeout(dismissPanel, HOVER_HIDE_DELAY_MS);
    };

    React.useEffect(() => {
        if (!panel) {
            return;
        }

        const onScroll = () => dismissPanel();
        window.addEventListener("scroll", onScroll, true);
        return () => window.removeEventListener("scroll", onScroll, true);
    }, [panel]);

    React.useEffect(() => () => clearHideTimer(), []);

    const handleAdjust = async (delta: number, event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        const nextSortOrder = sortOrder + delta;
        if (nextSortOrder < 0) {
            return;
        }

        dismissPanel();
        await onAdjust(nextSortOrder);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="btn p-0 border-0 text-muted"
                style={{ fontSize: "0.72rem", background: "transparent", lineHeight: 1 }}
                onClick={onEdit}
                onMouseEnter={openPanel}
                onMouseLeave={scheduleHidePanel}
                title={hasUrgentEnqueue ? `${urgentTitle} · ${editTitle}` : editTitle}
                aria-label={hasUrgentEnqueue ? `${urgentTitle} · ${editTitle}` : editTitle}
                disabled={disabled}
            >
                {`#${sortOrder}`}
                {hasUrgentEnqueue && <ChevronsUp size={14} style={{ color: "var(--accent)" }} aria-hidden="true" />}
            </button>

            {panel && createPortal(
                <div
                    style={{ ...panelStyle, top: panel.top, left: panel.left }}
                    onMouseEnter={openPanel}
                    onMouseLeave={scheduleHidePanel}
                    onClick={(event) => event.stopPropagation()}
                >
                    {ADJUST_BUTTONS.map(({ delta, icon: Icon, title }) => (
                        <button
                            key={delta}
                            type="button"
                            className="btn btn-sm p-0 border-0 text-muted"
                            style={adjustButtonStyle}
                            title={title}
                            disabled={disabled || (delta < 0 && sortOrder <= 0)}
                            onClick={(event) => handleAdjust(delta, event)}
                        >
                            <Icon className="sort-adjust-icon" aria-hidden="true" />
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </>
    );
};

export default SortOrderCell;
